import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';
import { type ProcessorDetails, useProcessorById, useUpdateProcessor } from '@/lib/api/processors';
import { useProcessorEditId } from '@/app/processors/hooks/use-processor-edit-id';
import { useProcessorEditExit } from '@/app/processors/hooks/use-processor-edit-exit';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SecretInput } from '@/components/form/secret-input';
import { useToast } from '@/components/ui/use-toast';
import { ProcessorEditHeader } from '../../components/processor-edit-header';
import { ProcessorDryModeField } from '../../components/processor-dry-mode-field';
import { validateXoalaCredentials } from './validation';

/** Xoala's `attemptThreeD`, mirrored from `src/lib/payment/processors/xoala/types.ts`. */
const ATTEMPT_THREE_D = ['3D', 'Only3D', 'Direct'] as const;
type AttemptThreeD = (typeof ATTEMPT_THREE_D)[number];
const DEFAULT_ATTEMPT_THREE_D: AttemptThreeD = '3D';

const isAttemptThreeD = (value: unknown): value is AttemptThreeD =>
  ATTEMPT_THREE_D.some((mode) => mode === value);

interface XoalaFormState {
  name: string;
  enabled: boolean;
  memberId: string;
  terminalId: string;
  partnerId: string;
  username: string;
  secretKey: string;
  attemptThreeD: AttemptThreeD;
  testMode: boolean;
  dryMode: boolean;
  /** Not editable here: off until the acquirer confirms external MPI. Carried so a save keeps it. */
  externalMpi: boolean;
}

const readString = (options: Record<string, unknown> | null | undefined, key: string): string => {
  const value = options?.[key];
  return typeof value === 'string' ? value : '';
};

const formStateFromProcessor = (processor: ProcessorDetails): XoalaFormState => {
  const options = processor.options;
  const attemptThreeD = options?.attemptThreeD;
  return {
    name: processor.name,
    enabled: processor.enabled ?? true,
    memberId: readString(options, 'memberId'),
    terminalId: readString(options, 'terminalId'),
    partnerId: readString(options, 'partnerId'),
    username: readString(options, 'username'),
    secretKey: readString(options, 'secretKey'),
    attemptThreeD: isAttemptThreeD(attemptThreeD) ? attemptThreeD : DEFAULT_ATTEMPT_THREE_D,
    testMode: options?.testMode === true,
    dryMode: options?.dryMode === true,
    externalMpi: options?.externalMpi === true,
  };
};

const XoalaEditPageInner = () => {
  const id = useProcessorEditId();
  const { t } = useTranslation('processors');
  const { data: processor, isLoading } = useProcessorById(id);

  if (isLoading) {
    return <div className="container mx-auto py-8" />;
  }

  if (!processor || processor.type !== 'xoala') {
    return (
      <div className="container mx-auto py-8">
        <Alert variant="destructive">
          <AlertDescription>{t('processorNotFound')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return <XoalaEditForm processor={processor} />;
};

type Props = {
  processor: ProcessorDetails;
};

const XoalaEditForm = ({ processor }: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation('processors');
  const { toast } = useToast();
  const processorExit = useProcessorEditExit();
  const updateProcessorMutation = useUpdateProcessor();

  const [validationError, setValidationError] = useState<string | null>(null);
  const [formData, setFormData] = useState<XoalaFormState>(() => formStateFromProcessor(processor));

  const validate = (): string | null => {
    const error = validateXoalaCredentials(formData);
    return error ? t(error) : null;
  };

  const handleSubmit = async () => {
    const error = validate();
    setValidationError(error);
    if (error) return;

    try {
      await updateProcessorMutation.mutateAsync({
        processorId: processor.id,
        updatedData: {
          type: processor.type,
          name: formData.name,
          enabled: formData.enabled,
          options: {
            memberId: formData.memberId.trim(),
            terminalId: formData.terminalId.trim(),
            partnerId: formData.partnerId.trim(),
            username: formData.username.trim(),
            secretKey: formData.secretKey.trim(),
            attemptThreeD: formData.attemptThreeD,
            externalMpi: formData.externalMpi,
            testMode: formData.testMode,
            dryMode: formData.dryMode,
          },
        },
      });

      toast({ description: t('processorUpdated') });
      if (processorExit.isEmbed) {
        processorExit.exitAfterSave();
        return;
      }
      navigate(-1);
    } catch {
      toast({ description: t('failedToSave'), variant: 'destructive' });
    }
  };

  const handleCancel = () => {
    if (processorExit.isEmbed) {
      processorExit.exitCancel();
      return;
    }
    navigate(-1);
  };

  const handleAttemptThreeDChange = (value: string) => {
    if (isAttemptThreeD(value)) setFormData({ ...formData, attemptThreeD: value });
  };

  return (
    <div className="container mx-auto py-8">
      <ProcessorEditHeader
        processorId={processor.id}
        title={t('xoala.editTitle')}
        onCancel={handleCancel}
        onSave={handleSubmit}
        isSaving={updateProcessorMutation.isPending}
      />

      {validationError && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{validationError}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('generalSettings')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name">{t('processorName')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked === true })}
              />
              <Label htmlFor="enabled">{t('enabled')}</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('apiCredentials')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
              <p className="text-sm text-blue-700">
                <strong>{t('xoala.credentials.howToGetTitle')}</strong>
              </p>
              <ol className="mt-2 list-decimal pl-4 text-sm text-blue-700">
                <li>{t('xoala.credentials.step1')}</li>
                <li>
                  <Trans i18nKey="xoala.credentials.step2" ns="processors" components={{ b: <b /> }} />
                </li>
                <li>{t('xoala.credentials.step3')}</li>
              </ol>
            </div>

            <div>
              <Label htmlFor="memberId">{t('xoala.credentials.memberIdLabel') + ' *'}</Label>
              <Input
                id="memberId"
                value={formData.memberId}
                onChange={(e) => setFormData({ ...formData, memberId: e.target.value })}
                inputMode="numeric"
                placeholder="11344"
                className="font-mono"
              />
            </div>

            <div>
              <Label htmlFor="terminalId">{t('xoala.credentials.terminalIdLabel') + ' *'}</Label>
              <Input
                id="terminalId"
                value={formData.terminalId}
                onChange={(e) => setFormData({ ...formData, terminalId: e.target.value })}
                inputMode="numeric"
                placeholder="1106"
                className="font-mono"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('xoala.credentials.terminalIdDescription')}
              </p>
            </div>

            <div>
              <Label htmlFor="partnerId">{t('xoala.credentials.partnerIdLabel') + ' *'}</Label>
              <Input
                id="partnerId"
                value={formData.partnerId}
                onChange={(e) => setFormData({ ...formData, partnerId: e.target.value })}
                placeholder="489"
                className="font-mono"
              />
            </div>

            <div>
              <Label htmlFor="username">{t('xoala.credentials.usernameLabel') + ' *'}</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                autoComplete="off"
              />
              <p className="text-muted-foreground mt-1 text-xs">
                {t('xoala.credentials.usernameDescription')}
              </p>
            </div>

            <SecretInput
              label={t('xoala.credentials.secretKeyLabel') + ' *'}
              value={formData.secretKey}
              onChange={(value) => setFormData({ ...formData, secretKey: value })}
              placeholder="••••••••"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('xoala.threeDS.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <Label htmlFor="attemptThreeD">{t('xoala.threeDS.label')}</Label>
            <Select value={formData.attemptThreeD} onValueChange={handleAttemptThreeDChange}>
              <SelectTrigger id="attemptThreeD" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ATTEMPT_THREE_D.map((mode) => (
                  <SelectItem key={mode} value={mode}>
                    {t(`xoala.threeDS.options.${mode}.label`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-muted-foreground text-sm">
              {t(`xoala.threeDS.options.${formData.attemptThreeD}.hint`)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('testModeSettings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="testMode"
                checked={formData.testMode}
                onCheckedChange={(checked) => setFormData({ ...formData, testMode: checked === true })}
              />
              <Label htmlFor="testMode">{t('enableTestMode')}</Label>
            </div>
            <p className="text-muted-foreground mt-1 text-xs">{t('xoala.testModeDescription')}</p>
          </CardContent>
        </Card>

        <ProcessorDryModeField
          checked={formData.dryMode}
          onCheckedChange={(checked) => setFormData({ ...formData, dryMode: checked })}
          disabled={updateProcessorMutation.isPending}
        />
      </div>
    </div>
  );
};

export const XoalaEditPage = withRequiredPermission(XoalaEditPageInner, PERMISSIONS.PROCESSORS_MANAGE);
